import React from 'react';
import { Warning } from '@phosphor-icons/react';

interface OverdueRow {
    employeeName: string;
    courseTitle: string;
    progressPercent: number;
    dueDate: string;
}

interface TeamOverdueCoursesWidgetProps {
    data?: OverdueRow[] | null;
    loading?: boolean;
    error?: string;
}

const getOverdueDays = (dueDate: string): number => {
    const now = new Date();
    const due = new Date(dueDate);
    return Math.floor((now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
};

export const TeamOverdueCoursesWidget: React.FC<TeamOverdueCoursesWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 pt-4 pb-4 flex items-center justify-between">
                {rows.length > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-red-100 text-red-700">
                        {rows.length}
                    </span>
                )}
            </div>
            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Warning className="w-8 h-8" />
                        <span className="text-sm">No overdue courses</span>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] text-gray-400 uppercase tracking-wider">
                                <th className="pb-2 font-medium">Person</th>
                                <th className="pb-2 font-medium">Course</th>
                                <th className="pb-2 font-medium text-right">Progress</th>
                                <th className="pb-2 font-medium text-right">Overdue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, idx) => {
                                const overdueDays = getOverdueDays(row.dueDate);
                                const isSevere = overdueDays > 14;
                                return (
                                    <tr
                                        key={idx}
                                        className={isSevere ? 'bg-red-50' : overdueDays > 7 ? 'bg-amber-50' : ''}
                                    >
                                        <td className="py-1.5 pr-2 text-gray-800 truncate max-w-[100px]">{row.employeeName}</td>
                                        <td className="py-1.5 pr-2 text-gray-600 truncate max-w-[120px]">{row.courseTitle}</td>
                                        <td className="py-1.5 text-right text-gray-500">{Number(row.progressPercent) || 0}%</td>
                                        <td className="py-1.5 text-right">
                                            <span className={`text-xs font-medium ${isSevere ? 'text-red-600' : 'text-amber-600'}`}>
                                                {overdueDays}d
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};
